import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import {
  SettingsRestrictionNotice,
  useSettings,
} from 'pages/SettingsV2/SettingsContext';
import { SettingsSectionKey } from 'pages/SettingsV2/settingsTypes';

interface SettingsV2SectionCardProps {
  sectionKey: SettingsSectionKey;
  title: string;
  icon: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode;
  disabled?: boolean;
  notice?: SettingsRestrictionNotice | null;
}

const SettingsV2SectionCard = ({
  sectionKey,
  title,
  icon,
  children,
  headerActions,
  disabled = false,
  notice = null,
}: SettingsV2SectionCardProps) => {
  const { t } = useTranslation();
  const { isDirty, savingSection, saveSection, cancelSection } = useSettings();
  const dirty = isDirty(sectionKey);
  const saving = savingSection === sectionKey;

  return (
    <Card className="shadow-none border mb-0">
      <CardHeader className="bg-transparent border-bottom">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                dirty
                  ? 'bg-warning-subtle text-warning'
                  : 'bg-success-subtle text-success'
              }`}
              style={{ width: 38, height: 38 }}
            >
              <i
                className={`fs-5 ${
                  dirty ? 'ri-error-warning-line' : icon
                }`}
              />
            </div>

            <div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h5 className="card-title mb-0 fw-semibold">{title}</h5>
              </div>
              <p
                className={`mb-0 mt-1 fs-13 ${
                  dirty ? 'text-warning' : 'text-muted'
                }`}
              >
                {dirty ? t('You have unsaved changes') : t('All changes saved')}
              </p>
            </div>
          </div>

          {!disabled ? (
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              {headerActions ? <div>{headerActions}</div> : null}
              <div className="d-flex gap-2">
                <Button
                  color="light"
                  className="w-100 w-sm-auto"
                  disabled={!dirty || saving}
                  onClick={() => cancelSection(sectionKey)}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  color="primary"
                  className="w-100 w-sm-auto d-inline-flex align-items-center justify-content-center"
                  disabled={!dirty || saving}
                  onClick={() => saveSection(sectionKey)}
                  aria-label={saving ? t('Saving...') : t('Save')}
                >
                  {saving ? <Spinner size="sm" /> : t('Save')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </CardHeader>
      {notice ? (
        <div className="px-3 px-lg-4 pt-3">
          <div className="border rounded-3 bg-light-subtle px-3 py-3 d-flex align-items-start gap-3">
            <div className="text-warning fs-4 lh-1">
              <i className="ri-lock-2-line" />
            </div>
            <div>
              <div className="fw-semibold text-body">{t(notice.titleKey)}</div>
              <div className="text-muted fs-13 mt-1">{t(notice.bodyKey)}</div>
              {notice.ctaKey ? (
                <div className="text-primary fs-13 fw-semibold mt-2">
                  {t(notice.ctaKey)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <CardBody className={disabled ? 'opacity-50 pe-none' : undefined}>
        {children}
      </CardBody>
    </Card>
  );
};

export default SettingsV2SectionCard;
